var __setFunctionName = (this && this.__setFunctionName) || function (f, name, prefix) {
    if (typeof name === "symbol") name = name.description ? "[".concat(name.description, "]") : "";
    return Object.defineProperty(f, "name", { configurable: true, value: prefix ? "".concat(prefix, " ", name) : name });
};
/* eslint-disable @typescript-eslint/no-explicit-any */
import { adoptStyles, getCompatibleStyle, unsafeCSS, } from 'lit';
import { customElement } from 'lit/decorators.js';
import { THEME_CHANGE_EVENT, registerComponents, setUIOptions, getTheme, adoptScripts, processComponentStyles, } from 'tinijs';
import { GLOBAL_TINI, TINI_APP_CONTEXT, APP_ROOT, ComponentTypes, LifecycleHooks, NO_REGISTER_ERROR, } from './consts';
import { isClass, getDIRegistry, getOptions, getConfigs, getContext, getApp, getSplashscreen, hideSplashscreen, registerGlobalHook, } from './methods';
import ___checkForDIMissingDependencies from './di-checker';
export function App(options = {}) {
    return function (target) {
        var _a;
        var _b;
        // set app options
        TINI_APP_CONTEXT.options = options;
        // ui options
        if (options.uiOptions) {
            setUIOptions(options.uiOptions);
        }
        // register the exit of the app splashscreen
        if (options.splashscreen) {
            registerGlobalHook(ComponentTypes.Page, LifecycleHooks.OnChildrenReady, ({ appContext }) => {
                var _a;
                if (((_a = appContext.options) === null || _a === void 0 ? void 0 : _a.splashscreen) !== 'auto')
                    return;
                hideSplashscreen();
            });
        }
        // dependencies management
        const dependencyRegistry = getDIRegistry();
        const providers = options.providers || {};
        const providerIds = Object.keys(providers);
        for (let i = 0; i < providerIds.length; i++) {
            const id = providerIds[i];
            const value = providers[id];
            // extract the provider and deps
            const { provider, deps } = !(value === null || value === void 0 ? void 0 : value.provider)
                ? { provider: value, deps: [] }
                : value;
            // the register
            let theRegister = dependencyRegistry.registers.get(id);
            if (theRegister)
                return; // already registered
            theRegister = async () => {
                let result = dependencyRegistry.instances.get(id);
                // already initialized
                if (result)
                    return;
                // resolve deps
                const depInstances = [];
                if (deps === null || deps === void 0 ? void 0 : deps.length) {
                    for (let j = 0; j < deps.length; j++) {
                        const depId = deps[j];
                        let depInstance = dependencyRegistry.instances.get(depId);
                        if (!depInstance) {
                            const theDepRegister = dependencyRegistry.registers.get(depId);
                            if (!theDepRegister)
                                throw NO_REGISTER_ERROR(depId);
                            depInstance = await theDepRegister();
                        }
                        depInstances.push(depInstance);
                    }
                }
                // result
                const m = await provider();
                const dependency = !m.default ? m : m.default;
                result = !isClass(dependency)
                    ? dependency
                    : new dependency(...depInstances);
                // <DEVELOPMENT>
                if (process.env.NODE_ENV === 'development') {
                    ___checkForDIMissingDependencies(id, dependency, result);
                }
                // </DEVELOPMENT>
                return dependencyRegistry.instances.set(id, result).get(id);
            };
            dependencyRegistry.registers.set(id, theRegister).get(id);
        }
        // digest the await queue
        if ((_a = dependencyRegistry.awaiters) === null || _a === void 0 ? void 0 : _a.length) {
            for (let i = 0; i < dependencyRegistry.awaiters.length; i++) {
                dependencyRegistry.awaiters[i]();
            }
        }
        // target modifications
        const result = (_b = class extends target {
                constructor(...args) {
                    super(...args);
                    // set the app & register components
                    GLOBAL_TINI.app = this;
                    if (options.components)
                        registerComponents(options.components);
                }
            },
            __setFunctionName(_b, "result"),
            _b.componentType = ComponentTypes.App,
            _b);
        // forward the target
        return customElement(APP_ROOT)(result);
    };
}
export function Component(options = {}) {
    return function (target) {
        var _a;
        // target modifications
        const result = (_a = class extends target {
                constructor() {
                    super(...arguments);
                    this.activeTheme = getTheme();
                    this.onThemeChange = (e) => {
                        this.activeTheme = e.detail;
                        this.customAdoptStyles(this.shadowRoot || this);
                        this.customAdoptScripts();
                    };
                }
                createRenderRoot() {
                    var _b;
                    const renderRoot = (_b = this.shadowRoot) !== null && _b !== void 0 ? _b : this.attachShadow(this.constructor.shadowRootOptions);
                    this.customAdoptStyles(renderRoot);
                    return renderRoot;
                }
                connectedCallback() {
                    if (options.components)
                        registerComponents(options.components);
                    super.connectedCallback();
                    addEventListener(THEME_CHANGE_EVENT, this.onThemeChange);
                }
                disconnectedCallback() {
                    super.disconnectedCallback();
                    removeEventListener(THEME_CHANGE_EVENT, this.onThemeChange);
                }
                updated(changedProperties) {
                    super.updated(changedProperties);
                    this.customAdoptScripts();
                }
                customAdoptStyles(renderRoot) {
                    var _b;
                    const allStyles = [];
                    // theme styles
                    const styling = (_b = options.theming) === null || _b === void 0 ? void 0 : _b.styling;
                    if (styling) {
                        const { soulId, themeId } = this.activeTheme;
                        allStyles.push(...(styling[themeId] ||
                            styling[soulId] ||
                            Object.values(styling)[0] ||
                            []));
                    }
                    // element styles
                    allStyles.push(...this.constructor.elementStyles);
                    // adopt styles
                    const styleText = processComponentStyles(allStyles, this.activeTheme);
                    adoptStyles(renderRoot, [
                        getCompatibleStyle(unsafeCSS(styleText)),
                    ]);
                }
                customAdoptScripts() {
                    var _b;
                    adoptScripts(this, this.activeTheme, (_b = options.theming) === null || _b === void 0 ? void 0 : _b.scripting);
                }
            },
            __setFunctionName(_a, "result"),
            _a.componentType = options.type || ComponentTypes.Component,
            _a);
        // return or forward the target
        return !options.name ? result : customElement(options.name)(result);
    };
}
export function Page(options) {
    return Component({ ...options, type: ComponentTypes.Page });
}
export function Layout(options) {
    return Component({ ...options, type: ComponentTypes.Layout });
}
export function Inject(id) {
    return function (prototype, propertyName) {
        const depId = (id || propertyName);
        const dependencyRegistry = getDIRegistry();
        // the pending
        const pending = async () => {
            let result = dependencyRegistry.instances.get(depId);
            if (!result) {
                const register = dependencyRegistry.registers.get(depId);
                if (!register)
                    throw NO_REGISTER_ERROR(depId);
                result = dependencyRegistry.instances
                    .set(depId, await register())
                    .get(depId);
            }
            return result;
        };
        // queue the dependencies
        const theRegister = dependencyRegistry.registers.get(depId);
        prototype.pendingDependencies || (prototype.pendingDependencies = []);
        if (!theRegister) {
            let resolveSchedule = () => { };
            const scheduledPending = new Promise(resolve => (resolveSchedule = resolve));
            dependencyRegistry.awaiters.push(() => resolveSchedule());
            prototype.pendingDependencies.push(() => scheduledPending.then(() => pending()));
        }
        else {
            prototype.pendingDependencies.push(pending);
        }
        // result
        Object.defineProperty(prototype, propertyName, {
            get: () => dependencyRegistry.instances.get(depId),
        });
    };
}
export function Vendor(id) {
    return Inject(id);
}
export function UseContext() {
    return function (prototype, propertyName) {
        Object.defineProperty(prototype, propertyName, {
            get: () => getContext(),
        });
    };
}
export function UseApp() {
    return function (prototype, propertyName) {
        Object.defineProperty(prototype, propertyName, {
            get: () => getApp(),
        });
    };
}
export function UseOptions() {
    return function (prototype, propertyName) {
        Object.defineProperty(prototype, propertyName, {
            get: () => getOptions(),
        });
    };
}
export function UseConfigs() {
    return function (prototype, propertyName) {
        Object.defineProperty(prototype, propertyName, {
            get: () => getConfigs(),
        });
    };
}
export function UseSplashscreen() {
    return function (prototype, propertyName) {
        Object.defineProperty(prototype, propertyName, {
            get: () => getSplashscreen(),
        });
    };
}
//# sourceMappingURL=decorators.js.map